/**
 * RegCardTermsCard — edit the "For Office Use Only" clauses printed on the
 * guest Registration Card, per hotel. Backed by /api/fo/settings/reg-card.
 */
import { useCallback, useEffect, useState } from "react";
import { foApi } from "@/pages/fo/foApi";
import { formatApiError } from "@/lib/api";
import { Card } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Plus, Trash2, RotateCcw, Save, Loader2 } from "lucide-react";

export default function RegCardTermsCard({ hotelId }) {
  const [terms, setTerms] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!hotelId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await foApi.regCardSettings(hotelId);
      setTerms(data.terms || []);
      setDefaults(data.defaults || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);

  const updateTerm = (i, v) => setTerms(terms.map((t, idx) => (idx === i ? v : t)));
  const removeTerm = (i) => setTerms(terms.filter((_, idx) => idx !== i));
  const addTerm = () => setTerms([...terms, ""]);
  const resetToDefaults = () => setTerms([...defaults]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = terms.map(t => t.trim()).filter(Boolean);
      const data = await foApi.saveRegCardSettings(hotelId, cleaned);
      setTerms(data.terms || []);
      toast.success("Registration card terms saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!hotelId) return null;

  return (
    <Card className="p-6 mt-4" data-testid="reg-card-terms-card">
      <div className="flex items-start justify-between gap-4 mb-4 pb-3 border-b border-hocp-border">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-hocp-textMuted" />
          <div>
            <div className="text-[13px] font-semibold text-hocp-textPrimary">Registration Card Terms</div>
            <div className="text-[11px] text-hocp-textMuted">
              The &ldquo;For Office Use Only&rdquo; clauses printed on every guest registration card.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            disabled={loading || saving}
            className="h-8 px-3 text-[12px] border-hocp-border"
            data-testid="reg-terms-reset-button"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to defaults
          </Button>
          <Button
            onClick={save}
            disabled={loading || saving}
            className="h-8 px-3 text-[12px] bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold"
            data-testid="reg-terms-save-button"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save terms
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-hocp-textMuted text-[13px] py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading terms…
        </div>
      ) : (
        <div className="space-y-3">
          {terms.length === 0 && (
            <div className="text-[12px] text-hocp-textMuted py-2">
              No terms configured. Add a clause or reset to defaults.
            </div>
          )}
          {terms.map((t, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`reg-term-row-${i}`}>
              <div className="flex-1">
                <Label className="sr-only" htmlFor={`reg-term-${i}`}>Clause {i + 1}</Label>
                <Textarea
                  id={`reg-term-${i}`}
                  value={t}
                  onChange={(e) => updateTerm(i, e.target.value)}
                  rows={2}
                  placeholder="Enter clause text…"
                  className="bg-hocp-bgCard border-hocp-border text-[13px]"
                  data-testid={`reg-term-input-${i}`}
                />
              </div>
              <Button
                variant="ghost"
                onClick={() => removeTerm(i)}
                className="h-8 w-8 p-0 text-hocp-textMuted hover:text-red-400 mt-0.5"
                aria-label={`Remove clause ${i + 1}`}
                data-testid={`reg-term-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            onClick={addTerm}
            className="h-8 px-3 text-[12px] border-hocp-border border-dashed"
            data-testid="reg-term-add-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add clause
          </Button>
        </div>
      )}
    </Card>
  );
}
